import { getStorageValue, setStorageValue } from '@portal-internet/core';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useLaMevaLlista } from 'hooks/3catinfo/useLaMevaLlista';
import { useUser } from 'hooks/useUser';
import { useData } from 'hooks/useData';
import { useModal } from 'hooks/3catinfo/useModal';
import { hitAudiencies } from 'components/3catinfo/Audiencies/audiencies-datalayer';
import __isUndefined from 'lodash/isUndefined';
import __isEmpty from 'lodash/isEmpty';
import Boto from 'components/3catinfo/Boto';
import LlistatFirmes from '../../LlistatFirmes';
import Icona from '../../ui/Icona';
import StickyFooter from '../../AreaUsuaris/StickyFooter';
import clsx from 'clsx';
import styles from './onboarding.module.scss';

// --- HELPER D'ADOBE TARGET PER CLICS ---
const trackTargetClick = (clickMbox, variant) => {
    const trackEvent = globalThis?.window?.adobe?.target?.trackEvent;

    if (typeof trackEvent === 'function') {
        console.log(`🎯 Adobe Target: Trackejant clic a [${clickMbox}] - Variant [${variant}]`);

        trackEvent({
            mbox: clickMbox,
            params: {
                variant: variant || 'unknown',
                elementTag: 'button',
                elementText: 'Guardar selecció'
            },
            success: () => {
                console.log('✅ Adobe Target: Clic enviat correctament');
            },
            error: (status, error) => {
                console.warn('❌ Adobe Target: Error enviant el clic', status, error);
            }
        });
    } else {
        console.warn('⚠️ Adobe Target: La funció trackEvent no està disponible');
    }
};

// --- HELPER D'ADOBE TARGET ---
const fetchTargetVariant = async (mbox, fallbackVariant = 'A') => {
    const timeout = 3000;
    const interval = 100;
    const start = Date.now();
    let target = null;

    while (Date.now() - start < timeout) {
        target = globalThis?.window?.adobe?.target;
        if (target) break;
        await new Promise((r) => setTimeout(r, interval));
    }

    if (!target) return fallbackVariant;

    return new Promise((resolve) => {
        try {
            if (typeof target.getOffer === 'function') {
                target.getOffer({
                    mbox,
                    success: (offer) => {
                        let variantRaw = offer?.[0]?.content?.[0]?.version;

                        const variant = typeof variantRaw === 'string' ? variantRaw.trim().toUpperCase() : fallbackVariant;

                        resolve(variant);
                    },
                    error: (error) => {
                        console.warn('Error obtenint Adobe Target Offer', error);
                        resolve(fallbackVariant);
                    }
                });
            } else {
                resolve(fallbackVariant);
            }
        } catch (error) {
            console.warn('Error obtenint Adobe Target Offer', error);
            resolve(fallbackVariant);
        }
    });
};

export default function Component(props) {
    const { layout, onboardingActiu } = props;
    const tipologia = 'NOT_AUTOR';

    const { setModal, removeModal } = useModal();
    const router = useRouter();

    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showFirmes, setShowFirmes] = useState(false);
    const [registreState, setRegistreState] = useState({});
    const [itemsFirmes, setItemsFirmes] = useState(false);
    const [showedOnboarding, setShowedOnboarding] = useState(true);
    const [cookieChecked, setCookieChecked] = useState(false);
    const [translateY, setTranslateY] = useState(0);
    const [targetVariant, setTargetVariant] = useState(null);

    const { data, getDades } = useData();
    const { signUp, signIn, loginState, loginStateTypes, state: userState, name, isAuthenticated } = useUser();

    const {
        addItemToAPI,
        removeItemToAPI,
        itemsLaMevaLlista,
        updateItemsLaMevaLlista,
        itemsLaMevaLlistaCloneRef,
        stickyVisible,
        setStickyVisible
    } = useLaMevaLlista();

    const timeoutShowOnboarding = useRef(null);
    const refOnboarding = useRef(null);
    const itemsLaMevaLlistaRef = useRef(itemsLaMevaLlista);
    const startY = useRef(0);
    const isMountedRef = useRef(true);
    const initialFetchDone = useRef(false);

    const SHOW_INTERVAL_DAYS = onboardingActiu?.horesCadaQuantEsMostra ? onboardingActiu?.horesCadaQuantEsMostra / 24 : 7;
    const MAX_ONBOARDING = onboardingActiu?.numMaxCopsQueEsMostra || null;
    const usuarisLogats = onboardingActiu?.usuarisLogats || false;
    const usuarisNoLogats = onboardingActiu?.usuarisNoLogats || false;
    const CLOSE_THRESHOLD = 120;
    const isTest =
        typeof window !== 'undefined' && window.Cypress && window?.Cypress?.spec?.baseName !== 'Onboarding.test.feature';

    const nomCookie = 'onboarding_3catinfo';

    const estructuraCookieDefault = { day: '2020/01/01', count: 0, variant: null };
    const isEmbedOrPopup = router.asPath.includes('/popup/') || router.asPath.includes('/embed/');
    const isRedl = router?.query?.editmode === 'true' || router?.query?.editmode === 'yes';

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            clearTimeout(timeoutShowOnboarding.current);
        };
    }, []);

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const isAvisActive = () => layout?.structure?.find((item) => item.name === 'ModulAvis')?.finalProps?.item?.length > 0;

    const getToday = () => {
        const dateObj = new Date();
        return `${dateObj.getUTCFullYear()}/${dateObj.getUTCMonth() + 1}/${dateObj.getUTCDate()}`;
    };

    const saveOnStorage = (values) =>
        setStorageValue(nomCookie, values, {
            path: '/3catinfo',
            domain: '.3cat.cat',
            expires: 7
        });

    const initializeValues = (data) => (__isUndefined(data) ? estructuraCookieDefault : data);

    const mostrarOnboarding = (userLoginState) => {
        if (isAuthenticated === true) return usuarisLogats;
        if (isAuthenticated === false) return usuarisNoLogats;

        // Fallback while auth status is still being resolved.
        return userLoginState === loginStateTypes.GUEST ? usuarisNoLogats : usuarisLogats;
    };

    const fetchDayCookieData = async () => {
        const data = await getStorageValue(nomCookie);
        if (!isMountedRef.current) return;
        let value = initializeValues(data);
        const hasPassed7Days = new Date() - new Date(value.day) >= SHOW_INTERVAL_DAYS * 24 * 60 * 60 * 1000;

        // Si ja existeix variant a la cookie, usar-la; si no, demanar a Adobe Target
        let variant = value.variant;
        if (!variant) {
            variant = await fetchTargetVariant('3catinfo-onboarding-firmes-mbox', 'A');
            console.log(`🎯 Adobe Target [3catinfo-onboarding-firmes-mbox]: Assignat a la Variant [${variant}]`);
        } else {
            console.log(`🎯 Adobe Target: Variant prèvia de la cookie [${variant}]`);
        }

        setTargetVariant(variant);
        setShowedOnboarding(!hasPassed7Days);
        setCookieChecked(true);
    };

    useEffect(() => {
        if (!initialFetchDone.current) {
            fetchDayCookieData();
            initialFetchDone.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isRedl) return;
        if (cookieChecked && !showedOnboarding && mostrarOnboarding(loginState) && targetVariant !== null) {
            getStorageValue(nomCookie).then((data) => {
                const value = initializeValues(data);
                if (MAX_ONBOARDING === null || value.count < MAX_ONBOARDING) {
                    timeoutShowOnboarding.current = setTimeout(() => {
                        if (isMountedRef.current) {
                            setShowOnboarding(true);
                        }
                    }, 3500);
                }
            });
        } else if (!mostrarOnboarding(loginState)) {
            setShowOnboarding(false);
        }

        return () => clearTimeout(timeoutShowOnboarding.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginState, isAuthenticated, cookieChecked, showedOnboarding, targetVariant]);

    useEffect(() => {
        const hash = router.asPath.split('#')[1];
        const parsedHash = new URLSearchParams(hash);
        const encodedState = parsedHash.get('state');
        if (encodedState && !router.pathname?.includes('usuaris-redirect')) {
            try {
                const decodedState = decodeURIComponent(encodedState);
                const stateParams = JSON.parse(decodedState);
                setRegistreState(stateParams);
            } catch {
                setRegistreState({});
            }
        }
    }, [router]);

    useEffect(() => {
        if (
            (userState &&
                userState.router === router.pathname &&
                userState.namespace === '[web]onboarding-firmes-login' &&
                userState.onboardingFirmes) ||
            (registreState.router === router.pathname &&
                registreState.namespace === '[web]onboarding-firmes-registre' &&
                registreState.onboardingFirmes)
        ) {
            setShowFirmes(true);
            //router.replace(router.pathname, undefined, { shallow: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState, registreState]);

    useEffect(() => {
        if (showFirmes) {
            getDades(
                'https://api.3cat.cat/v2/autor?_format=json&origen=frontal&frontal=n324-trescatinf-totesfirme&items_pagina=30&sdom=img&version=2.0&cache=90&https=true&master=yes&perfils_extra=imatges_3catinfo'
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showFirmes]);

    useEffect(() => {
        if (!isMountedRef.current) return;
        setItemsFirmes(data);
        setStickyVisible(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        itemsLaMevaLlistaRef.current = itemsLaMevaLlista;
    }, [itemsLaMevaLlista]);

    const handleTouchStart = (e) => {
        startY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
        const deltaY = e.touches[0].clientY - startY.current;
        if (deltaY > 0) setTranslateY(deltaY);
    };

    const handleTouchEnd = () => {
        if (translateY > CLOSE_THRESHOLD) {
            tancaOnboarding();
        } else {
            setTranslateY(0);
        }
    };

    const tancaOnboarding = () => {
        removeModal();
        setShowOnboarding(false);
        setShowedOnboarding(true);
        setShowFirmes(false);
    };

    const getArrayDiffs = (original = [], updated = []) => ({
        toRemove: original.filter((id) => !updated.includes(id)),
        toAdd: updated.filter((id) => !original.includes(id))
    });

    const onSaveTrigger = async () => {
        tancaOnboarding();

        const original = itemsLaMevaLlistaCloneRef.current[tipologia] || [];
        const updated = itemsLaMevaLlistaRef.current[tipologia] || [];
        const { toRemove, toAdd } = getArrayDiffs(original, updated);

        try {
            for (const item of toRemove) {
                await removeItemToAPI(item, tipologia);
                await sleep(50);
            }
            for (const item of toAdd) {
                await addItemToAPI(item, tipologia);
                await sleep(50);
            }

            itemsLaMevaLlistaCloneRef.current[tipologia] = [...updated];
            setStickyVisible(false);
            router.push('/3catinfo/meu3catinfo/firmes/');
        } catch (error) {
            console.error('Error actualitzant seleccions:', error);
        }
    };

    const onCancelTrigger = () => {
        updateItemsLaMevaLlista(itemsLaMevaLlistaCloneRef.current);
        setStickyVisible(false);
        tancaOnboarding();
    };

    const contentOnBoardingFirmes = () => (
        <div ref={refOnboarding} className={styles.mask} role='dialog' aria-modal='true' aria-labelledby='onboarding-titol'>
            <div className={clsx(styles.onBoarding, styles.onBoardingFirmes)}>
                <button className={styles.botoTanca} onClick={tancaOnboarding}>
                    <Icona icona='tanca' alt='Tanca popup "Crea un compte i personalitza la teva experiència"' />
                </button>

                <div
                    className={styles.rectangleWrapper}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <span className={styles.rectangle}></span>
                </div>

                <div className={styles.onBoardingContent}>
                    <div className={styles.textWrapper}>
                        <div className={styles.nomWrapper}>
                            Hola, <span className={styles.nom}>{name}</span>
                        </div>

                        <div className={styles.descripcio}>
                            Tria les firmes que t&apos;interessen i podràs seguir-les a &quot;El meu 3CatInfo&quot;
                        </div>
                    </div>
                    <div className={clsx(styles.llistatWrapper, { [styles.stickyShow]: !!stickyVisible })}>
                        <LlistatFirmes
                            numColumns={1}
                            estilMosaic='firmesOnboarding'
                            estilAutor='onboarding'
                            className={styles.mosaicFirmes}
                            items={itemsFirmes}
                            obreEsquerra={true}
                            enviaApi={false}
                        />
                    </div>
                </div>

                {itemsLaMevaLlistaCloneRef.current && (
                    <StickyFooter
                        estil='onBoardingFirmes'
                        tipologia={tipologia}
                        onUpdate={onSaveTrigger}
                        onCancel={onCancelTrigger}
                        isOnModal
                    />
                )}
                {!stickyVisible && (
                    <div className={styles.mesTardContent}>
                        <span role='button' className={styles.mesTard} onClick={() => tancaOnboarding()}>
                            Fes-ho més tard
                        </span>
                    </div>
                )}
            </div>
        </div>
    );

    const contentOnBoarding = () => (
        <div
            data-testid={`onboarding-firmes`}
            ref={refOnboarding}
            className={styles.mask}
            role='dialog'
            aria-modal='true'
            aria-labelledby='onboarding-titol'
        >
            <div className={clsx(styles.onBoarding, styles.onBoardingLogin)}>
                <div
                    className={styles.rectangleWrapper}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <span className={styles.rectangle}></span>
                </div>

                <div className={styles.contentWrapper}>
                    <div className={styles.firmes}></div>
                    <div className={styles.content}>
                        <div className={styles.contentText}>
                            <h2 className={styles.titol} id='onboarding-titol'>
                                Segueix les firmes{' '}
                                <span className={styles.breakTitol}>de 3CatInfo PoC (Variant {targetVariant})</span>
                            </h2>
                            <p className={styles.text}>
                                Amb el teu compte 3Cat pots tenir tots els articles dels periodistes que més t&apos;interessen a
                                <span className={styles.breakTarget}>&quot;El meu 3CatInfo&quot;</span>
                            </p>
                        </div>
                        <div className={styles.buttons}>
                            {' '}
                            <div className={styles.buttonsContent}>
                                <Boto
                                    tag='button'
                                    text='Inicia sessió'
                                    estil='ple3cat100'
                                    className={styles.boto}
                                    onClick={() => {
                                        hitAudiencies('scClicBoto', {
                                            URL: window.location.href,
                                            identificadorClic: `Modal login 3catinfo - botó login`
                                        });
                                        trackTargetClick('3catinfo-onboarding-firmes-mbox-click', targetVariant);
                                        signUp({ onboardingFirmes: true, namespace: '[web]onboarding-firmes-login' });
                                    }}
                                />

                                <Boto
                                    tag='button'
                                    estil='lined3cat100'
                                    className={styles.boto}
                                    text='Crea el teu compte 3Cat'
                                    onClick={() => {
                                        hitAudiencies('scClicBoto', {
                                            URL: window.location.href,
                                            identificadorClic: `Modal login 3catinfo - botó registre`
                                        });
                                        trackTargetClick('3catinfo-onboarding-firmes-mbox-click', targetVariant);
                                        signIn({
                                            onboardingFirmes: true,
                                            namespace: '[web]onboarding-firmes-registre',
                                            router: router.pathname
                                        });
                                    }}
                                />
                            </div>
                            <div>
                                <span role='button' className={styles.mesTard} onClick={() => tancaOnboarding()}>
                                    Fes-ho més tard
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    useEffect(() => {
        const isOpen = showOnboarding || showFirmes;
        if (isRedl) return;
        if (isOpen) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
        } else {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }

        return () => {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOnboarding, showFirmes]);

    useEffect(() => {
        if (
            showOnboarding &&
            !isRedl &&
            !isTest &&
            !showedOnboarding &&
            !isEmbedOrPopup &&
            !isAvisActive() &&
            targetVariant !== null
        ) {
            setModal(contentOnBoarding());
            getStorageValue(nomCookie).then((data) => {
                const value = initializeValues(data);
                const today = getToday();

                // Guardar la cookie: actualitzar count/day i variant
                value.day = today;
                value.count = (value.count || 0) + 1;
                value.variant = targetVariant;
                saveOnStorage(value);
            });
        } else if (showFirmes && !isRedl && targetVariant !== null) {
            setModal(contentOnBoardingFirmes());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOnboarding, showFirmes, showedOnboarding, isEmbedOrPopup, itemsFirmes, stickyVisible, targetVariant]);

    return null;
}
